import { useEffect, useMemo, useState, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useProducts } from "@/contexts/ProductsContext";
import { isOfferActive, getDiscountLabel } from "@/utils/offerUtils";
import { productPath } from "@/utils/shareLinks";

const FLASH_DURATION_MS = 48 * 60 * 60 * 1000;
const fallbackFlashOffer = () => ({
 enabled: true as const,
 type: "percentage" as const,
 value: 10,
 endDate: new Date(Date.now() + FLASH_DURATION_MS).toISOString(),
});

// Context so Navbar can read whether the bar is showing
const DiscountBarContext = createContext<{ visible: boolean; dismiss: () => void }>({ visible: false, dismiss: () => {} });
export const useDiscountBar = () => useContext(DiscountBarContext);

export const DiscountBarProvider = ({ children }: { children: React.ReactNode }) => {
 const visible = true;
 const dismiss = () => {};
 return (
 <DiscountBarContext.Provider value={{ visible, dismiss }}>
 {children}
 </DiscountBarContext.Provider>
 );
};

const StickyDiscountBar = () => {
 const { visible } = useDiscountBar();
 const { isAr } = useLanguage();
 const { products } = useProducts();
 const flashProducts = useMemo(
 () => products.filter((product) => product.flashDealEligible && product.stockStatus !== "out_of_stock" && product.stockStatus !== "sold_out"),
 [products]
 );
 const [activeIndex, setActiveIndex] = useState(0);

 useEffect(() => {
 if (flashProducts.length <= 1) return;
 const interval = window.setInterval(() => {
 setActiveIndex((current) => (current + 1) % flashProducts.length);
 }, 4200);
 return () => window.clearInterval(interval);
 }, [flashProducts.length]);

 useEffect(() => {
 if (activeIndex >= flashProducts.length) setActiveIndex(0);
 }, [activeIndex, flashProducts.length]);

 if (!visible) return null;

 const activeProduct = flashProducts[activeIndex];
 const activeOffer = activeProduct && isOfferActive(activeProduct.offer) ? activeProduct.offer : fallbackFlashOffer();
 const message = activeProduct
 ? isAr
 ? `${getDiscountLabel(activeOffer, true)} على ${activeProduct.nameAr}`
 : `${getDiscountLabel(activeOffer)} on ${activeProduct.name}`
 : isAr
 ? "خصم 10% على أول طلب, الكود: NILBEAUTY10"
 : "10% Off Your First Order, Use Code: NILBEAUTY10";
 const content = (
 <div className="flex min-h-10 items-center justify-center gap-2 sm:gap-2.5 py-2 sm:py-2.5">
 <Zap size={14} strokeWidth={2.4} className="text-background/90 shrink-0 fill-current"  aria-hidden="true" focusable="false" />
 <p key={activeProduct?.id ?? "first-order"} className="animate-fade-in font-body text-[11px] sm:text-sm font-semibold tracking-wide sm:tracking-wider text-center text-background drop-shadow-sm leading-snug">
 {isAr ? "عرض خاطف:" : "Flash Deal:"} {message}
 </p>
 </div>
 );

 return (
 <div className="relative brand-gradient-flow rounded-sm my-1.5 px-3 sm:px-6 overflow-hidden">
 <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px shimmer-line" />
 <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-px shimmer-line" />
 {activeProduct ? (
 <Link to={productPath(activeProduct.id)} className="block transition-opacity duration-300 hover:opacity-90">
 {content}
 </Link>
 ) : content}
 </div>
 );
};

export default StickyDiscountBar;
